import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Award } from 'lucide-react';
import LabelHeading from '../../common/LabelHeading';

const FAQBenefitsSection = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      id: 0,
      question: "What is staff augmentation?",
      answer: "Staff augmentation is a workforce strategy where businesses hire skilled professionals on a temporary or contract basis to support ongoing projects. It can help or increase productivity—without long-term hiring commitments."
    },
    {
      id: 1,
      question: "How Does Off Site SEO Work?",
      answer: "Off-site SEO involves activities performed outside of your website to improve its search engine rankings, including link building, social media marketing, and influencer outreach."
    },
    {
      id: 2,
      question: "What is off page SEO link building?",
      answer: "Off-page SEO link building is the process of acquiring backlinks from other websites to improve your site's authority and search engine rankings."
    },
    {
      id: 3,
      question: "How to Create a Chatbot to Fit Your Needs?",
      answer: "Creating a chatbot involves defining your goals, choosing the right platform, designing conversation flows, and implementing AI to handle customer interactions effectively."
    },
    {
      id: 4,
      question: "How can I order web design for my website?",
      answer: "You can order web design services by contacting our team, discussing your requirements, reviewing our portfolio, and selecting a package that fits your needs and budget."
    },
    {
      id: 5,
      question: "Best SEO Practices for Page Layouts",
      answer: "Best SEO practices for page layouts include proper heading structure, mobile responsiveness, fast loading times, clear navigation, and strategic placement of keywords."
    },
    {
      id: 6,
      question: "Why is Researching Keywords Important?",
      answer: "Keyword research is crucial for understanding what your target audience is searching for, helping you create relevant content and improve your search engine visibility."
    }
  ];

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <div className=" bg-white">
      <div className="w-full max-w-[1400px] mx-auto px-6 pb-14">
        {/* FAQ Section */}
        <div className="text-center mb-12">
          <LabelHeading label={'FAQ'} heading={'Frequently Asked Questions'} />
          <p data-aos="fade-up" data-aos-duration="1000" className="text-gray-600 max-w-3xl mx-auto">
            We are proud to be recognized with the Magnit Impact Player Award, celebrating our strong partnership, market expertise.
          </p>
        </div>

        {/* FAQ Grid */}
        <div
          data-aos="fade-up"
          data-aos-duration="1000"
          className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-20"
        >
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className={`rounded-lg border transition-all duration-300 ${openFAQ === faq.id
                ? "bg-blue-50 border-blue-200"
                : "bg-gray-50 border-gray-200 hover:border-gray-300"
                }`}
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
              >
                <span
                  className={`font-semibold ${openFAQ === faq.id ? "text-blue-900" : "text-gray-900"
                    }`}
                >
                  {faq.question}
                </span>
                {openFAQ === faq.id ? (
                  <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0 ml-2" />
                )}
              </button>

              {/* Animated answer */}
              <div
                className={`px-6 overflow-hidden transition-all duration-500 ease-in-out ${openFAQ === faq.id ? "max-h-40 opacity-100 pb-4" : "max-h-0 opacity-0"
                  }`}
              >
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>


        {/* Employee Benefits Section */}
        <div className="mt-20">
          <h2 data-aos="fade-up" data-aos-duration="1000" className="text-3xl sm:text-4xl font-semibold font-SpaceGrotesk text-primaryColor mb-6">
            Unlocking Employee Benefits
          </h2>
          <p data-aos="fade-up" data-aos-duration="1000" className="text-gray-600 mb-12 leading-relaxed">
            We believe that happy and fulfilled employees are the cornerstone of any successful organization. As a leading staffing services provider, we go above and beyond to provide a range of benefits that enhance the overall well-being and satisfaction of our employees. By choosing to partner with us, you gain access to a multitude of advantages designed to support and reward your talent. Explore the numerous benefits our company offers.
          </p>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Section */}
            <div data-aos="fade-up-right" data-aos-duration="1000" className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1552581234-26160f608093?w=600&h=700&fit=crop"
                alt="Team meeting"
                className="w-full h-[500PX] object-cover rounded-lg shadow-lg"
              />
            </div>

            {/* Benefits Content */}
            <div className="order-1 lg:order-2 space-y-8">
              {/* Benefit 1 */}
              <div data-aos="fade-up" data-aos-duration="1000" data-aos-delay="100" className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Award className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold font-SpaceGrotesk text-primaryColor mb-3">
                    Competitive Compensation Packages
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    We understand the value of your skills and expertise, which is why we offer highly competitive compensation packages. Our goal is to ensure that your hard work and dedication are appropriately rewarded. By joining our team, you can expect a fair and competitive salary that reflects your qualifications and experience.
                  </p>
                </div>
              </div>

              {/* Benefit 2 */}
              <div data-aos="fade-up" data-aos-duration="1000" data-aos-delay="200" className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Award className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold font-SpaceGrotesk text-primaryColor mb-3">
                    Competitive Compensation Packages
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Your well-being is our priority. We provide Minimum Essential Coverage (MEC) & Indemnity Plans to protect you and your loved ones. Our coverage ensures that your healthcare needs are taken care of:
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li data-aos="fade-up" data-aos-duration="1000" data-aos-delay="100" className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Minimum Essential Coverage (MEC)</span>
                    </li>
                    <li data-aos="fade-up" data-aos-duration="1000" data-aos-delay="200" className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Fixed Indemnity Plan</span>
                    </li>
                    <li data-aos="fade-up" data-aos-duration="1000" data-aos-delay="300" className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>MEC Enhanced Advantage Plan</span>
                    </li>
                    <li data-aos="fade-up" data-aos-duration="1000" data-aos-delay="400" className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Treetanding Coverage Options</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQBenefitsSection;